import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { addEdge, Background, Controls, MiniMap, Position, useEdgesState, useNodesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  createWebhookEndpoint,
  deleteWebhookEndpoint,
  getWebhookMapping,
  getWebhookWorkflow,
  listWebhookEndpoints,
  listWebhookEvents,
  listWebhookRuns,
  regenerateWebhookEndpointKey,
  saveWebhookMapping,
  saveWebhookWorkflow,
  updateWebhookEndpoint,
} from '../services/webhookConsumerApi';

const defaultNodes = [
  {
    id: 'webhook',
    position: { x: 60, y: 80 },
    data: { label: 'Webhook Trigger', type: 'trigger' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'transform',
    position: { x: 320, y: 80 },
    data: { label: 'Transform Payload', type: 'transform' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'http',
    position: { x: 580, y: 80 },
    data: { label: 'HTTP Request', type: 'http_request', url: '' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
];
const defaultEdges = [
  { id: 'e1', source: 'webhook', target: 'transform' },
  { id: 'e2', source: 'transform', target: 'http' },
];

const flattenKeys = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return flattenKeys(value, path);
    }
    return [path];
  });
};

const WebhookConsumer = () => {
  const { getAuthHeaders } = useAuth();
  const [systemMessage, setSystemMessage] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [endpointDraft, setEndpointDraft] = useState({
    name: '',
    auth_username: '',
    auth_password: '',
    target_url: '',
    enabled: true,
  });
  const [mappingDraft, setMappingDraft] = useState({
    source_schema: {},
    target_schema: {},
    field_mappings: [],
  });
  const [workflowDraft, setWorkflowDraft] = useState({
    name: 'Default Workflow',
    graph_json: { nodes: defaultNodes, edges: defaultEdges },
  });
  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(defaultEdges);
  const [events, setEvents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(() =>
    typeof window === 'undefined' ? 720 : Math.round(window.innerWidth * 0.7)
  );
  const [drawerResizing, setDrawerResizing] = useState(false);
  const drawerResizeRef = useState({ startX: 0, startWidth: 720 })[0];
  const [drawerMode, setDrawerMode] = useState('edit');
  const [expandedEventIds, setExpandedEventIds] = useState({});
  const [eventFilters, setEventFilters] = useState({ startAt: '', endAt: '', search: '' });
  const [nodeIdCounter, setNodeIdCounter] = useState(4);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapperRef = useRef(null);
  const lastDragPosRef = useRef(null);
  const placingTypeRef = useRef('');
  const [isPlacingNode, setIsPlacingNode] = useState(false);
  const [placingPreview, setPlacingPreview] = useState({ x: 0, y: 0, type: '' });

  const sourceFields = useMemo(() => flattenKeys(mappingDraft.source_schema), [mappingDraft.source_schema]);
  const targetFields = useMemo(() => flattenKeys(mappingDraft.target_schema), [mappingDraft.target_schema]);

  const loadEndpoints = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const resp = await listWebhookEndpoints(headers);
      setEndpoints(resp.data || []);
      if (resp.data?.length && !selectedEndpoint) {
        setSelectedEndpoint(resp.data[0]);
      }
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to load endpoints.' });
    }
  }, [getAuthHeaders, selectedEndpoint]);

  const loadEndpointDetails = useCallback(async () => {
    if (!selectedEndpoint) return;
    try {
      const headers = getAuthHeaders();
      const [mappingResp, workflowResp, eventsResp, runsResp] = await Promise.all([
        getWebhookMapping(selectedEndpoint.id, headers),
        getWebhookWorkflow(selectedEndpoint.id, headers),
        listWebhookEvents(selectedEndpoint.id, headers, {
          start_at: eventFilters.startAt || undefined,
          end_at: eventFilters.endAt || undefined,
          search: eventFilters.search || undefined,
        }),
        listWebhookRuns(selectedEndpoint.id, headers),
      ]);
      const mapping = mappingResp.data || {};
      setMappingDraft({
        source_schema: mapping.source_schema || {},
        target_schema: mapping.target_schema || {},
        field_mappings: mapping.field_mappings || [],
      });
      const workflow = workflowResp.data || {};
      const graph = workflow.graph_json || { nodes: defaultNodes, edges: defaultEdges };
      const normalizedNodes = (graph.nodes || defaultNodes).map((node) => ({
        ...node,
        type: node.type || 'default',
        sourcePosition: node.sourcePosition || Position.Right,
        targetPosition: node.targetPosition || Position.Left,
      }));
      setWorkflowDraft({ name: workflow.name || 'Default Workflow', graph_json: graph });
      setNodes(normalizedNodes);
      setEdges(graph.edges || defaultEdges);
      setEvents(eventsResp.data || []);
      setRuns(runsResp.data || []);
    } catch (err) {
      setSystemMessage({ type: 'warning', text: 'Failed to load mapping/workflow.' });
    }
  }, [eventFilters, getAuthHeaders, selectedEndpoint, setEdges, setNodes]);

  useEffect(() => {
    loadEndpoints();
  }, [loadEndpoints]);

  useEffect(() => {
    loadEndpointDetails();
  }, [loadEndpointDetails]);

  const handleSelectEndpoint = (endpoint, mode = 'edit') => {
    setSelectedEndpoint(endpoint);
    setEndpointDraft({
      name: endpoint.name || '',
      auth_username: endpoint.auth_username || '',
      auth_password: '',
      target_url: endpoint.target_url || '',
      enabled: endpoint.enabled ?? true,
    });
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const handleNewEndpoint = () => {
    setSelectedEndpoint(null);
    setDrawerMode('create');
    setEndpointDraft({
      name: '',
      auth_username: '',
      auth_password: '',
      target_url: '',
      enabled: true,
    });
    setMappingDraft({
      source_schema: {},
      target_schema: {},
      field_mappings: [],
    });
    setWorkflowDraft({
      name: 'Default Workflow',
      graph_json: { nodes: defaultNodes, edges: defaultEdges },
    });
    setNodes(defaultNodes);
    setEdges(defaultEdges);
    setDrawerOpen(true);
  };

  const handleCreateEndpoint = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const resp = await createWebhookEndpoint(endpointDraft, headers);
      setSystemMessage({ type: 'success', text: 'Endpoint created.' });
      setSelectedEndpoint(resp.data);
      await loadEndpoints();
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to create endpoint.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEndpoint = async () => {
    if (!selectedEndpoint) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await updateWebhookEndpoint(selectedEndpoint.id, endpointDraft, headers);
      setSystemMessage({ type: 'success', text: 'Endpoint updated.' });
      await loadEndpoints();
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to update endpoint.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEndpoint = async (endpointOverride = null) => {
    const endpoint = endpointOverride || selectedEndpoint;
    if (!endpoint) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await deleteWebhookEndpoint(endpoint.id, headers);
      setSystemMessage({ type: 'success', text: 'Endpoint deleted.' });
      if (selectedEndpoint?.id === endpoint.id) {
        setSelectedEndpoint(null);
      }
      await loadEndpoints();
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to delete endpoint.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateEndpointKey = async () => {
    if (!selectedEndpoint) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const resp = await regenerateWebhookEndpointKey(selectedEndpoint.id, headers);
      setSystemMessage({ type: 'success', text: 'Endpoint URL regenerated.' });
      setSelectedEndpoint(resp.data);
      await loadEndpoints();
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to regenerate endpoint URL.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedEndpoint) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await saveWebhookMapping(selectedEndpoint.id, mappingDraft, headers);
      setSystemMessage({ type: 'success', text: 'Mapping saved.' });
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to save mapping.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWorkflow = async () => {
    if (!selectedEndpoint) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const graph_json = { nodes, edges };
      await saveWebhookWorkflow(selectedEndpoint.id, { ...workflowDraft, graph_json }, headers);
      setSystemMessage({ type: 'success', text: 'Workflow saved.' });
    } catch (err) {
      setSystemMessage({ type: 'danger', text: 'Failed to save workflow.' });
    } finally {
      setLoading(false);
    }
  };

  const addMappingRow = () => {
    setMappingDraft((prev) => ({
      ...prev,
      field_mappings: [...prev.field_mappings, { source: '', target: '' }],
    }));
  };

  const updateMappingRow = (index, patch) => {
    setMappingDraft((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.map((row, idx) => (idx === index ? { ...row, ...patch } : row)),
    }));
  };

  const removeMappingRow = (index) => {
    setMappingDraft((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.filter((_, idx) => idx !== index),
    }));
  };

  const handleNodeChange = (id, dataPatch) => {
    setNodes((prev) => prev.map((node) => (node.id === id ? { ...node, data: { ...node.data, ...dataPatch } } : node)));
  };

  useEffect(() => {
    if (!reactFlowInstance) return;
    if (nodes.length) {
      reactFlowInstance.fitView({ padding: 0.2 });
    }
  }, [nodes, reactFlowInstance]);

  const handleConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  const addWorkflowNode = (type) => {
    const id = `node_${nodeIdCounter}`;
    setNodeIdCounter((prev) => prev + 1);
    const baseNode = {
      id,
      position: { x: 80 + nodeIdCounter * 30, y: 240 + nodeIdCounter * 10 },
      data: { label: 'Step', type },
      type: 'default',
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
    const config = {
      trigger: { label: 'Webhook Trigger' },
      transform: { label: 'Transform' },
      http_request: { label: 'HTTP Request', url: '' },
      filter: { label: 'Filter' },
      delay: { label: 'Delay' },
      log: { label: 'Log' },
    };
    setNodes((prev) => [...prev, { ...baseNode, data: { ...baseNode.data, ...config[type] } }]);
  };

  const placeNodeAtClient = (clientX, clientY, type) => {
    if (!type || !reactFlowInstance || !reactFlowWrapperRef.current) return false;
    const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
    const inside =
      clientX >= bounds.left &&
      clientX <= bounds.right &&
      clientY >= bounds.top &&
      clientY <= bounds.bottom;
    if (!inside) return false;
    const position = reactFlowInstance.screenToFlowPosition({
      x: clientX,
      y: clientY,
    });
    const id = `node_${nodeIdCounter}`;
    setNodeIdCounter((prev) => prev + 1);
    const config = {
      trigger: { label: 'Webhook Trigger' },
      transform: { label: 'Transform' },
      http_request: { label: 'HTTP Request', url: '' },
      filter: { label: 'Filter' },
      delay: { label: 'Delay' },
      log: { label: 'Log' },
    };
    setNodes((prev) => [
      ...prev,
      {
        id,
        position,
        type: 'default',
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        data: { label: 'Step', type, ...config[type] },
      },
    ]);
    window.requestAnimationFrame(() => {
      reactFlowInstance.setCenter(position.x, position.y, { zoom: 1 });
    });
    return true;
  };


  useEffect(() => {
    if (!isPlacingNode) return undefined;
    const handleMove = (event) => {
      lastDragPosRef.current = { x: event.clientX, y: event.clientY };
      const bounds = reactFlowWrapperRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const inside =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;
      if (inside) {
        setPlacingPreview({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
          type: placingTypeRef.current || '',
        });
      } else {
        setPlacingPreview((prev) => ({ ...prev, type: '' }));
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsPlacingNode(false);
        placingTypeRef.current = '';
        setPlacingPreview({ x: 0, y: 0, type: '' });
      }
    };
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'crosshair';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlacingNode, nodeIdCounter, reactFlowInstance, setNodes]);

  const webhookBase =
    (process.env.REACT_APP_WEBHOOK_CONSUMER_API_BASE_URL || 'http://localhost:8002').replace(/\/$/, '');
  const webhookUrl = selectedEndpoint
    ? `${webhookBase}/api/v1/webhook/${selectedEndpoint.endpoint_key}`
    : '';

  const renderJson = (data) => (
    <pre className="json-block">{JSON.stringify(data || {}, null, 2)}</pre>
  );

  const toggleEventExpand = (eventId) => {
    setExpandedEventIds((prev) => ({ ...prev, [eventId]: !prev?.[eventId] }));
  };

  const copyEventData = (event) => {
    const payload = {
      id: event.id,
      received_at: event.received_at,
      status: event.status,
      headers: event.headers,
      payload: event.payload,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  };

  useEffect(() => {
    if (!drawerResizing) return undefined;
    const onMove = (event) => {
      const delta = drawerResizeRef.startX - event.clientX;
      const rawWidth = drawerResizeRef.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 420;
      setDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drawerResizing, drawerResizeRef]);

  return (
    <div className="webhook-consumer-page">
      {systemMessage && (
        <div className={`alert alert-${systemMessage.type} webhook-system-bar`} role="status">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>{systemMessage.text}</div>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setSystemMessage(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h3 className="mb-1">Webhook Consumer</h3>
          <div className="text-muted">Create endpoints, map payloads, and design workflows.</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={handleSaveMapping} disabled={loading || !selectedEndpoint}>
            Save Mapping
          </button>
          <button className="btn btn-primary" onClick={handleSaveWorkflow} disabled={loading || !selectedEndpoint}>
            Save Workflow
          </button>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h6 className="mb-0">All Consumer Endpoints</h6>
                <button className="btn btn-outline-primary" onClick={handleNewEndpoint}>
                  Add New Endpoint
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Status</th>
                      <th style={{ width: 260 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoints.map((endpoint) => (
                      <tr key={endpoint.id}>
                        <td>{endpoint.name}</td>
                        <td className="text-muted small">{endpoint.endpoint_key}</td>
                        <td>
                          <span className={`badge ${endpoint.enabled ? 'bg-success' : 'bg-secondary'}`}>
                            {endpoint.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => handleSelectEndpoint(endpoint, 'edit')}>
                              Edit
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleSelectEndpoint(endpoint, 'view')}>
                              View Data
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteEndpoint(endpoint)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!endpoints.length && (
                      <tr>
                        <td colSpan="4" className="text-muted text-center">
                          No endpoints yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="text-muted small">
                Use the actions above to add, edit, or view data for endpoints.
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .webhook-system-bar {
          position: sticky;
          top: 0;
          z-index: 20;
        }
        .workflow-canvas {
          height: 360px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
        }
        .workflow-preview {
          position: absolute;
          transform: translate(-50%, -50%);
          background: #1e293b;
          color: #f8fafc;
          padding: 6px 10px;
          border-radius: 8px;
          font-size: 12px;
          pointer-events: none;
          opacity: 0.85;
          z-index: 5;
          text-transform: capitalize;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.2);
        }
        .workflow-canvas .react-flow {
          width: 100%;
          height: 100%;
        }
        .workflow-canvas .react-flow__renderer {
          pointer-events: auto;
          z-index: 1;
        }
        .workflow-canvas .react-flow__viewport {
          z-index: 5;
        }
        .workflow-canvas .react-flow__nodes {
          z-index: 6;
          pointer-events: auto;
        }
        .workflow-canvas .react-flow__nodes > * {
          z-index: 6;
        }
        .workflow-canvas .react-flow__node {
          min-width: 160px;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #22c55e;
          background: #ffffff;
        }
        .workflow-canvas .react-flow__pane {
          cursor: grab;
        }
        .workflow-canvas .react-flow__node {
          cursor: grab;
        }
        .workflow-canvas .react-flow__node.selected {
          cursor: grabbing;
        }
        .webhook-url-inline {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 10px;
          display: inline-block;
          max-width: 100%;
          word-break: break-all;
        }
        .webhook-url code {
          display: block;
          padding: 6px 10px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        .field-list {
          display: flex;
          flex-wrap: wrap;
        }
        .json-block {
          background: #0f172a;
          color: #e2e8f0;
          padding: 10px;
          border-radius: 8px;
          font-size: 12px;
          max-height: 180px;
          overflow: auto;
        }
        .endpoint-drawer {
          position: fixed;
          top: 0;
          right: 0;
          height: 100vh;
          background: #ffffff;
          border-left: 1px solid #e2e8f0;
          box-shadow: -12px 0 24px rgba(15, 23, 42, 0.12);
          z-index: 2000;
          display: flex;
          flex-direction: column;
        }
        .endpoint-drawer__header {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .endpoint-drawer__body {
          padding: 20px;
          overflow-y: auto;
        }
        .drawer-handle {
          position: absolute;
          left: -6px;
          top: 0;
          width: 12px;
          height: 100%;
          cursor: col-resize;
        }
      `}</style>

      {drawerOpen && (
        <div className="endpoint-drawer" style={{ width: drawerWidth }}>
          <div
            className="drawer-handle"
            onMouseDown={(event) => {
              drawerResizeRef.startX = event.clientX;
              drawerResizeRef.startWidth = drawerWidth;
              setDrawerResizing(true);
            }}
          />
          <div className="endpoint-drawer__header">
            <div className="fw-semibold">
              {drawerMode === 'view'
                ? 'Webhook Events'
                : selectedEndpoint
                ? 'Edit Consumer Endpoint'
                : 'Add Consumer Endpoint'}
            </div>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setDrawerOpen(false)}>
              Close
            </button>
          </div>
          <div className="endpoint-drawer__body">
            {selectedEndpoint && drawerMode !== 'view' && (
              <div className="mb-3">
                <label className="form-label">Webhook URL</label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <code className="webhook-url-inline">{webhookUrl}</code>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigator.clipboard.writeText(webhookUrl)}
                  >
                    <Copy size={14} /> Copy
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRegenerateEndpointKey}
                    disabled={loading}
                  >
                    Regenerate URL
                  </button>
                </div>
              </div>
            )}
            {drawerMode !== 'view' && (
            <>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                value={endpointDraft.name}
                onChange={(e) => setEndpointDraft((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Target URL</label>
              <input
                className="form-control"
                value={endpointDraft.target_url}
                onChange={(e) => setEndpointDraft((prev) => ({ ...prev, target_url: e.target.value }))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Auth Username</label>
              <input
                className="form-control"
                value={endpointDraft.auth_username}
                onChange={(e) => setEndpointDraft((prev) => ({ ...prev, auth_username: e.target.value }))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Auth Password</label>
              <input
                type="password"
                className="form-control"
                value={endpointDraft.auth_password}
                onChange={(e) => setEndpointDraft((prev) => ({ ...prev, auth_password: e.target.value }))}
              />
            </div>
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                checked={endpointDraft.enabled}
                onChange={(e) => setEndpointDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
              <label className="form-check-label">Enabled</label>
            </div>
            <div className="d-flex gap-2 flex-wrap mb-4">
              <button className="btn btn-outline-primary" onClick={handleCreateEndpoint} disabled={loading}>
                Create
              </button>
              <button className="btn btn-primary" onClick={handleUpdateEndpoint} disabled={loading || !selectedEndpoint}>
                Update
              </button>
              <button className="btn btn-outline-danger" onClick={handleDeleteEndpoint} disabled={loading || !selectedEndpoint}>
                Delete
              </button>
            </div>

            <div className="border-top pt-3">
              <h6 className="mb-3">Mapping</h6>
              <div className="mb-3">
                <label className="form-label">Source Schema (JSON)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={JSON.stringify(mappingDraft.source_schema, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setMappingDraft((prev) => ({ ...prev, source_schema: parsed }));
                    } catch {}
                  }}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Target Schema (JSON)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={JSON.stringify(mappingDraft.target_schema, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setMappingDraft((prev) => ({ ...prev, target_schema: parsed }));
                    } catch {}
                  }}
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Field Mappings</label>
                  <button className="btn btn-sm btn-outline-primary" onClick={addMappingRow}>
                    Add
                  </button>
                </div>
                {mappingDraft.field_mappings.map((row, index) => (
                  <div key={`${index}-mapping`} className="border rounded p-2 mb-2">
                    <input
                      className="form-control mb-2"
                      placeholder="Source field"
                      value={row.source || ''}
                      onChange={(e) => updateMappingRow(index, { source: e.target.value })}
                      onDrop={(e) => {
                        e.preventDefault();
                        const value = e.dataTransfer.getData('text/plain');
                        updateMappingRow(index, { source: value });
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    />
                    <input
                      className="form-control"
                      placeholder="Target field"
                      value={row.target || ''}
                      onChange={(e) => updateMappingRow(index, { target: e.target.value })}
                    />
                    <button className="btn btn-sm btn-link text-danger mt-2" onClick={() => removeMappingRow(index)}>
                      Remove
                    </button>
                  </div>
                ))}
                {!mappingDraft.field_mappings.length && <div className="text-muted small">No mappings yet.</div>}
              </div>
              <div className="mb-2">
                <label className="form-label">Source Fields</label>
                <div className="field-list">
                  {sourceFields.map((field) => (
                    <span
                      key={field}
                      className="badge bg-light text-dark border me-1 mb-1"
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', field)}
                    >
                      {field}
                    </span>
                  ))}
                  {!sourceFields.length && <div className="text-muted small">No source fields yet.</div>}
                </div>
              </div>
              <div>
                <label className="form-label">Target Fields</label>
                <div className="field-list">
                  {targetFields.map((field) => (
                    <span key={field} className="badge bg-light text-dark border me-1 mb-1">
                      {field}
                    </span>
                  ))}
                  {!targetFields.length && <div className="text-muted small">No target fields yet.</div>}
                </div>
              </div>
            </div>
            <div className="border-top pt-3 mt-3">
              <h6 className="mb-3">Workflow Builder</h6>
              <div className="workflow-toolbox mb-3">
                <div className="fw-semibold small mb-2">Standard Blocks</div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('trigger')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'trigger';
                      setIsPlacingNode(true);
                    }}
                  >
                    Webhook Trigger
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('transform')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'transform';
                      setIsPlacingNode(true);
                    }}
                  >
                    Transform
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('filter')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'filter';
                      setIsPlacingNode(true);
                    }}
                  >
                    Filter
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('http_request')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'http_request';
                      setIsPlacingNode(true);
                    }}
                  >
                    HTTP Request
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('delay')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'delay';
                      setIsPlacingNode(true);
                    }}
                  >
                    Delay
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => !isPlacingNode && addWorkflowNode('log')}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      placingTypeRef.current = 'log';
                      setIsPlacingNode(true);
                    }}
                  >
                    Log
                  </button>
                </div>
              </div>
              <div
                className="workflow-canvas"
                ref={reactFlowWrapperRef}
              >
                {isPlacingNode && (
                  <div
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      const type = placingTypeRef.current;
                      if (!type) return;
                      const placed = placeNodeAtClient(event.clientX, event.clientY, type);
                      if (placed) {
                        setIsPlacingNode(false);
                        placingTypeRef.current = '';
                        setPlacingPreview({ x: 0, y: 0, type: '' });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 4,
                      cursor: 'crosshair',
                      background: 'transparent',
                    }}
                  />
                )}
                {isPlacingNode && placingPreview.type && (
                  <div
                    className="workflow-preview"
                    style={{
                      left: placingPreview.x,
                      top: placingPreview.y,
                    }}
                  >
                    {placingPreview.type.replace('_', ' ')}
                  </div>
                )}
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={handleConnect}
                  preventScrolling={false}
                  zoomOnScroll
                  onPaneClick={(event) => {
                    if (!isPlacingNode) return;
                    const type = placingTypeRef.current;
                    if (!type) return;
                    const placed = placeNodeAtClient(event.clientX, event.clientY, type);
                    if (placed) {
                      setIsPlacingNode(false);
                      placingTypeRef.current = '';
                      setPlacingPreview({ x: 0, y: 0, type: '' });
                    }
                  }}
                  onNodeClick={(event) => {
                    if (!isPlacingNode) return;
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  nodesDraggable
                  nodesConnectable
                  elementsSelectable
                  panOnDrag
                  panOnScroll
                  fitView
                  fitViewOptions={{ padding: 0.2 }}
                  style={{ width: '100%', height: '100%' }}
                  onInit={setReactFlowInstance}
                >
                  <Background />
                  <MiniMap />
                  <Controls />
                </ReactFlow>
              </div>
              <div className="mt-3">
                <label className="form-label">HTTP Request URL (workflow)</label>
                <input
                  className="form-control"
                  value={nodes.find((node) => node.id === 'http')?.data?.url || ''}
                  onChange={(e) => handleNodeChange('http', { url: e.target.value })}
                  placeholder="https://target-system/webhook"
                />
              </div>
            </div>
            </>
            )}

            {drawerMode === 'view' && (
              <div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <div>
                    <label className="form-label small">Start</label>
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={eventFilters.startAt}
                      onChange={(e) => setEventFilters((prev) => ({ ...prev, startAt: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="form-label small">End</label>
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={eventFilters.endAt}
                      onChange={(e) => setEventFilters((prev) => ({ ...prev, endAt: e.target.value }))}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <label className="form-label small">Search</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search payload or headers"
                      value={eventFilters.search}
                      onChange={(e) => setEventFilters((prev) => ({ ...prev, search: e.target.value }))}
                    />
                  </div>
                  <div className="align-self-end">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => loadEndpointDetails()}
                    >
                      Apply
                    </button>
                  </div>
                </div>
                {events.length === 0 && <div className="text-muted small">No events yet.</div>}
                {events.map((event) => {
                  const eventRuns = runs.filter((run) => run.event_id === event.id);
                  const failedRuns = eventRuns.filter((run) => run.status === 'failed');
                  const receivedFrom = event.headers?.client_ip || event.headers?.x_forwarded_for || 'Unknown';
                  return (
                    <div key={event.id} className="border rounded p-2 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <div className="fw-semibold">{selectedEndpoint?.name || 'Webhook Endpoint'}</div>
                          <div className="small text-muted">Event #{event.id}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">{event.received_at || ''}</div>
                          <span className="badge bg-secondary">{event.status || 'received'}</span>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div className="small text-muted">Received from: {receivedFrom}</div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => toggleEventExpand(event.id)}
                          >
                            {expandedEventIds[event.id] ? 'Hide Data' : 'View Data'}
                          </button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => copyEventData(event)}>
                            Copy
                          </button>
                        </div>
                      </div>
                      {expandedEventIds[event.id] && (
                        <div className="mt-3">
                          <div className="fw-semibold small">Payload</div>
                          {renderJson(event.payload)}
                          <div className="fw-semibold small mt-2">Headers & Client</div>
                          {renderJson(event.headers)}
                          {failedRuns.length > 0 && (
                            <div className="mt-2">
                              <div className="fw-semibold small text-danger">Failed Attempts</div>
                              {failedRuns.map((run) => (
                                <div key={run.id} className="border rounded p-2 mt-2">
                                  <div className="small">Run #{run.id}</div>
                                  <div className="small text-muted">{run.started_at || ''}</div>
                                  {run.logs?.error && <div className="text-danger small">{run.logs.error}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookConsumer;
